import { lazy } from 'react'
import type { ComponentType } from 'react'

/**
 * `lazy()`, but it survives a deploy.
 *
 * Every screen in this app is a separate chunk fetched on demand, named by
 * content hash. A deploy replaces those files, so a tab that has been open
 * since before it asks for `Layout-DwPgtKh0.js` and gets a 404 — React throws
 * "Failed to fetch dynamically imported module" and the screen (or the whole
 * workspace pane) dies. It happened in production on 2026-09-10, three deploys
 * into an afternoon, in a tab that had been open the whole time.
 *
 * Two halves to the fix. Kamal now bridges `public/vite` across deploys
 * (config/deploy.yml `asset_path`) so the *previous* build's chunks stay
 * served; and this, for a tab older than that: a missing chunk means the page
 * itself is stale, so reload once and let the fresh document ask for chunks
 * that exist. Once — a loop that reloads forever is worse than an error
 * message, so the second failure is thrown for the error boundary to show.
 */

const RELOADED = 'multimagic:stale-chunk-reloaded'

/** The shapes a browser reports a missing or unparseable module as. */
function isStaleChunk(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk \d+ failed/i.test(
    message,
  )
}

function alreadyReloaded(): boolean {
  try {
    return sessionStorage.getItem(RELOADED) === '1'
  } catch {
    // Private mode, blocked storage: better to show the error than to loop.
    return true
  }
}

function rememberReload(): void {
  try {
    sessionStorage.setItem(RELOADED, '1')
  } catch {
    /* nothing to remember it in */
  }
}

/** A promise that never settles, so nothing renders before the reload lands. */
const untilReload = <T,>(): Promise<T> => new Promise<T>(() => {})

export function retryOnStaleChunk<T>(load: () => Promise<T>): () => Promise<T> {
  return async () => {
    try {
      return await load()
    } catch (error) {
      if (!isStaleChunk(error) || alreadyReloaded()) throw error

      rememberReload()
      window.location.reload()
      return untilReload<T>()
    }
  }
}

/** Drop-in for `lazy(() => import('…'))`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyModule<T extends { default: ComponentType<any> }>(load: () => Promise<T>) {
  return lazy(retryOnStaleChunk(load))
}

/**
 * A tab that reloaded once and got a working page should be able to do it
 * again after the *next* deploy, so the mark is cleared when the app boots
 * successfully.
 */
export function clearStaleChunkMark(): void {
  try {
    sessionStorage.removeItem(RELOADED)
  } catch {
    /* nothing to clear */
  }
}
